import { groupBy, sumBy } from 'lodash';
import moment from 'moment';
import { Gantt } from '@bryntum/gantt';
import dayjs from 'dayjs';

// @flow
/**
 * @public
 */
export type FilterType = 'Year' | 'Month' | 'Week' | 'Day';

export type SourceDataType = {
  day: string;
  seriesName: string;
  [key: string]: any;
};

export type SeriesData = {
  name: string;
  values: number[];
};

export type FilterData = {
  timeline: string[];
  dataZoom?: any;
  series: SeriesData[];
};

const parseXAxisDate = (xStr: string) => {
  const [yearStr, monthPart] = xStr.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthPart.replace('Month', ''), 10);
  return { year, month };
};

export const parseMonthToDate = (monthStr: string): Date => {
  const [yearStr, monthPart] = monthStr.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthPart.replace('Month', ''), 10) - 1;
  return new Date(year, month, 1);
};

export const parseMonthToEndDate = (monthStr: string): Date => {
  const [yearStr, monthPart] = monthStr.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthPart.replace('Month', ''), 10);
  return new Date(year, month, 0);
};

const getDate = (monthStr: string, isEnd = true) => {
  const [yearStr, monthPart] = monthStr.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthPart.replace('Month', ''), 10);
  const day = dayjs(`${year}-${month}`);
  if (isEnd) {
    return day.endOf('month').toDate();
  } else {
    return day.startOf('month').toDate();
  }
};

export const syncEChartsToGantt = (ganttInstance: Gantt | null, xAxisData: string[]): void => {
  if (!ganttInstance || !xAxisData.length) return;
  // const limitStart = ganttInstance.project.startDate as Date;
  // const limitEnd = ganttInstance.project.endDate as Date;
  try {
    const startMonthStr = xAxisData[0];
    const endMonthStr = xAxisData[xAxisData.length - 1];
    if (!startMonthStr || !endMonthStr) return;
    const ganttStart = getDate(startMonthStr, false);
    const ganttEnd = getDate(endMonthStr);
    ganttInstance.setTimeSpan(ganttStart, ganttEnd);
  } catch (error) {
    console.error(error);
  }
};

export function updateTasksCheckStatus(tasksData: any[], allRecords: any[]): any[] {
  const checkMap = new Map<string, boolean | undefined>(allRecords.map((record) => [record.id, record.check]));

  const processNode = (node: any) => {
    if (checkMap.has(node.id)) {
      node.check = checkMap.get(node.id);
    }

    if (Array.isArray(node.children) && node.children.length > 0) {
      node.children.forEach((child: any) => processNode(child));
    }
  };

  // 处理顶层节点
  tasksData.forEach((node) => processNode(node));
  return tasksData;
}

export function syncTimeRangeToECharts(timeType: FilterType, ganttInstance: any, echartsInstance: any, xAxisData: string[], data: any[]) {
  if (!ganttInstance || !echartsInstance || !xAxisData.length || !timeType) return;
  const project = ganttInstance.project;
  const { visibleDateRange } = ganttInstance;

  // const ganttStartYear = visibleDateRange.startDate.getFullYear();
  // const ganttStartMonth = visibleDateRange.startDate.getMonth() + 1; // 1-12
  // const ganttEndYear = visibleDateRange.endDate.getFullYear();
  // const ganttEndMonth = visibleDateRange.endDate.getMonth() + 1;

  const { timeline, series } = filterDataByDate(timeType, data, project, ganttInstance.visibleDateRange);
  const totalData = timeline.map((_, index) =>
    series.reduce((sum, s) => sum + (s.values[index] || 0), 0),
  );

  echartsInstance.setOption({
    xAxis: { data: timeline },
    series: [
      { name: 'total', data: totalData },
      ...series.map((s) => ({ name: s.name, data: s.values })),
    ],
    dataZoom: [
      {
        startValue: visibleDateRange.startDate,
        endValue: visibleDateRange.endDate,
      },
    ],
  });
  return timeline;
}

export const getMaxAllowedRange = (ganttProject: any, visibleDateRange: any): any => {
  const { startDate, endDate } = ganttProject;
  if (!startDate || !endDate) {
    return null;
  }
  let visibleEnd = visibleDateRange.endDate;
  const dataZoom = { start: 0, end: 0, maxAllowedRange: 0, notUsed: true };

  const dayOfMonth = visibleEnd.getDate();
  if (dayOfMonth <= 10) {
    const adjustedEnd = new Date(visibleEnd);
    adjustedEnd.setMonth(adjustedEnd.getMonth() - 1);
    adjustedEnd.setDate(0);
    visibleEnd = adjustedEnd;
  }

  const totalDuration = endDate.getTime() - startDate.getTime();
  const visibleDuration = visibleEnd.getTime() - startDate.getTime();

  if (totalDuration <= 0) {
    dataZoom.end = 100;
    dataZoom.maxAllowedRange = 100;
  } else {
    const safeVisibleDuration = Math.max(visibleDuration, 0);
    const percentage = (safeVisibleDuration / totalDuration) * 100;
    dataZoom.end = Math.min(Math.round(percentage), 100); // 限制最大100%
    dataZoom.maxAllowedRange = dataZoom.end;
  }

  return dataZoom;
};

export const filterDataByDate = (
  timeType: FilterType,
  sourceData: SourceDataType[],
  ganttProject: any,
  visibleDateRange: any,
): FilterData => {
  const fullSourceData = sourceData.map((item) => {
    const dayMoment = moment(item.day, 'ddd MMM DD YYYY');
    return {
      ...item,
      day: dayMoment.format('YYYY-MM-DD'),
      week: dayMoment.isoWeek(),
      isoWeekYear: dayMoment.isoWeekYear(),
      month: new Date(item.day).getMonth() + 1,
      year: new Date(item.day).getFullYear(),
    };
  });

  const getMapping = (list) => {
    const map = {};
    list.forEach((element) => {
      let key;
      if (timeType === 'Week') {
        key = `${element.isoWeekYear}-Week${element.week}`;
      } else if (timeType === 'Month') {
        key = `${element.year}-Month${element.month}`;
      } else {
        key = element[timeType.toLowerCase()];
      }
      map[key] = map[key] || [];
      map[key].push(element);
    });
    return map;
  };

  const getValues = (timeline, object) => {
    const arr: any[] = [];
    timeline.forEach((key) => {
      let count = 0;
      const values = object[key];
      if (values && values.length > 0) {
        if (timeType !== 'Day') {
          const group = groupBy(values, 'day');
          const maxElement = Object.values(group).reduce((pre, cur) => {
            const preSum = sumBy(pre, 'value');
            const curSum = sumBy(cur, 'value');
            return preSum > curSum ? pre : cur;
          });
          count += sumBy(maxElement, 'value');
        } else {
          values.forEach((element) => {
            if (element.value) {
              count += element.value;
            }
          });
        }
      }
      arr.push(count);
    });
    return arr;
  };

  const { startDate, endDate } = visibleDateRange;
  // const { startDate, endDate } = ganttProject;
  const timeline = [];

  const getNextDate = (currentDate, timeType) => {
    const next = new Date(currentDate);
    switch (timeType) {
      case 'Day':
        next.setDate(next.getDate() + 1);
        break;
      case 'Week':
        next.setDate(next.getDate() + 7);
        break;
      case 'Month':
        next.setMonth(next.getMonth() + 1);
        next.setDate(1);
        break;
      case 'Year':
        next.setFullYear(next.getFullYear() + 1);
        next.setMonth(0, 1);
        break;
      default:
        throw new Error('Invalid timeType');
    }
    return next;
  };

  const formatDate = (date, timeType) => {
    const year = date.getFullYear();
    switch (timeType) {
      case 'Day':
        return `${year}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      case 'Week': {
        const dateMoment = moment(date);
        return `${dateMoment.isoWeekYear()}-Week${dateMoment.isoWeek()}`;
      }
      case 'Month':
        return `${year}-Month${date.getMonth() + 1}`;
      case 'Year':
        return year.toString();
      default:
        throw new Error('Invalid timeType');
    }
  };

  let currentDate = new Date(startDate);
  if (timeType === 'Month') {
    currentDate.setDate(1);
  } else if (timeType === 'Year') {
    currentDate.setMonth(0, 1);
  }

  while (currentDate <= endDate) {
    timeline.push(formatDate(currentDate, timeType));
    currentDate = getNextDate(currentDate, timeType);
  }

  const series = Array.from(new Set(fullSourceData.map((element) => element.seriesName)));

  const result: any[] = [];
  series.forEach((element) => {
    const find = getMapping(fullSourceData.filter((item) => item.seriesName === element));
    result.push({
      name: element,
      values: getValues(timeline, find),
    });
  });

  return {
    timeline,
    series: result,
  };
};
